import React, { createContext, useContext, useState, useEffect } from 'react';
import { PASSWORD_PROTECTION_CONFIG, TIME_HELPERS } from './config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionExpiry, setSessionExpiry] = useState(null);
  const [permissionLevel, setPermissionLevel] = useState(null);

  const logout = () => {
    setIsAuthenticated(false);
    setSessionExpiry(null);
    setPermissionLevel(null);
    localStorage.removeItem(PASSWORD_PROTECTION_CONFIG.AUTH_KEY);
    localStorage.removeItem(PASSWORD_PROTECTION_CONFIG.SESSION_EXPIRY_KEY);
    localStorage.removeItem(PASSWORD_PROTECTION_CONFIG.PERMISSION_LEVEL_KEY);
  };

  // Check if user is already authenticated on component mount
  useEffect(() => {
    const authStatus = localStorage.getItem(
      PASSWORD_PROTECTION_CONFIG.AUTH_KEY
    );
    const expiryTime = localStorage.getItem(
      PASSWORD_PROTECTION_CONFIG.SESSION_EXPIRY_KEY
    );
    const storedPermissionLevel = localStorage.getItem(
      PASSWORD_PROTECTION_CONFIG.PERMISSION_LEVEL_KEY
    );

    if (authStatus === 'true' && expiryTime && storedPermissionLevel) {
      const now = Date.now();
      const expiry = parseInt(expiryTime, 10);

      if (now < expiry) {
        setIsAuthenticated(true);
        setSessionExpiry(expiry);
        setPermissionLevel(storedPermissionLevel);
      } else {
        // Session has expired, clear authentication
        logout();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set up session timeout monitoring
  useEffect(() => {
    if (!isAuthenticated || !sessionExpiry) return;

    const checkSessionExpiry = () => {
      const now = Date.now();
      if (now >= sessionExpiry) {
        logout();
      }
    };

    // Check at configured interval
    const interval = setInterval(
      checkSessionExpiry,
      PASSWORD_PROTECTION_CONFIG.SESSION_CHECK_INTERVAL
    );

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, sessionExpiry]);

  const login = password => {
    let userPermissionLevel = null;

    // Check which password was used
    if (password === PASSWORD_PROTECTION_CONFIG.PASSWORDS.FULL_ACCESS) {
      userPermissionLevel =
        PASSWORD_PROTECTION_CONFIG.PERMISSION_LEVELS.FULL_ACCESS;
    } else if (
      password === PASSWORD_PROTECTION_CONFIG.PASSWORDS.CAREER_QUEST_ONLY
    ) {
      userPermissionLevel =
        PASSWORD_PROTECTION_CONFIG.PERMISSION_LEVELS.CAREER_QUEST_ONLY;
    } else {
      return false; // Invalid password
    }

    const expiry = TIME_HELPERS.getSessionExpiry();

    setIsAuthenticated(true);
    setSessionExpiry(expiry);
    setPermissionLevel(userPermissionLevel);
    localStorage.setItem(PASSWORD_PROTECTION_CONFIG.AUTH_KEY, 'true');
    localStorage.setItem(
      PASSWORD_PROTECTION_CONFIG.SESSION_EXPIRY_KEY,
      expiry.toString()
    );
    localStorage.setItem(
      PASSWORD_PROTECTION_CONFIG.PERMISSION_LEVEL_KEY,
      userPermissionLevel
    );
    return true;
  };

  const extendSession = () => {
    if (isAuthenticated) {
      const expiry = TIME_HELPERS.getSessionExpiry();

      setSessionExpiry(expiry);
      localStorage.setItem(
        PASSWORD_PROTECTION_CONFIG.SESSION_EXPIRY_KEY,
        expiry.toString()
      );
    }
  };

  const getSessionTimeRemaining = () => {
    if (!sessionExpiry) return 0;
    const now = Date.now();
    return Math.max(0, sessionExpiry - now);
  };

  const value = {
    isAuthenticated,
    permissionLevel,
    login,
    logout,
    extendSession,
    getSessionTimeRemaining,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
